import { useCallback, useContext } from 'react';
import { matchRoutes } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

import type { Decision } from '../domain/decide';
import { useAccessContext } from '../hooks';
import { reportBranchWouldDeny } from '../reporting';
import type { MeAccess } from '../types';

import { RouteTableContext } from './RouteTableContext';
import { decideBranch } from './routeAccess';
import type { BranchDecision } from './routeAccess';

const NOWHERE: Decision = { allow: false, reason: 'UNDECLARED' };

const NOT_FOUND_PATH = '*';

interface Visit {
  readonly branch: BranchDecision;
  readonly pathname: string;
}

const urlOf = (to: string): URL | null => {
  try {
    return new URL(to, 'http://app.local');
  } catch {
    return null;
  }
};

const computeVisit = (me: MeAccess, routes: readonly RouteObject[], to: string): Visit | null => {
  const url = urlOf(to);
  if (url === null) return null;
  const matches = matchRoutes([...routes], url.pathname);
  const leaf = matches?.[matches.length - 1];
  if (!matches || !leaf || leaf.route.path === NOT_FOUND_PATH) return null;
  const branch = decideBranch(
    me,
    matches.map((match) => ({ handle: match.route.handle, params: match.params })),
    url.searchParams,
  );
  return { branch, pathname: url.pathname };
};

const visitsByTable = new WeakMap<readonly RouteObject[], WeakMap<MeAccess, Map<string, Visit | null>>>();

const visitsFor = (routes: readonly RouteObject[], me: MeAccess): Map<string, Visit | null> => {
  const byMe = visitsByTable.get(routes) ?? new WeakMap<MeAccess, Map<string, Visit | null>>();
  visitsByTable.set(routes, byMe);
  const visits = byMe.get(me) ?? new Map<string, Visit | null>();
  byMe.set(me, visits);
  return visits;
};

const visitOf = (me: MeAccess, routes: readonly RouteObject[], to: string): Visit | null => {
  const visits = visitsFor(routes, me);
  if (!visits.has(to)) visits.set(to, computeVisit(me, routes, to));
  return visits.get(to) ?? null;
};

export const canVisitPath = (me: MeAccess, routes: readonly RouteObject[], to: string): Decision => visitOf(me, routes, to)?.branch.decision ?? NOWHERE;

export const useCanVisit = (): ((to: string) => boolean) => {
  const routes = useContext(RouteTableContext);
  if (routes === null) throw new Error('useCanVisit reads the route table, which App provides through RouteTableContext');
  const { me, policy } = useAccessContext();
  return useCallback(
    (to: string) => {
      const visit = visitOf(me, routes, to);
      if (visit === null) return false;
      if (visit.branch.decision.allow || policy === 'enforce') return visit.branch.decision.allow;
      reportBranchWouldDeny(visit.branch, visit.pathname);
      return true;
    },
    [routes, me, policy],
  );
};
